import {
  BPS_DENOMINATOR,
  checkedAddU128,
  floorFee,
  isRate,
  isU64,
  splitFloorFee,
} from '@morfi/market-math';

export type BackerRewardProjectionUnavailableReason =
  | 'invalid_amount'
  | 'invalid_config'
  | 'invalid_proposal_state'
  | 'missing_config';

export type BackerRewardProjection =
  | { status: 'empty' }
  | { status: 'unavailable'; reason: BackerRewardProjectionUnavailableReason }
  | {
      status: 'ready';
      assumedFinalSupplyD6: bigint;
      userSharesAfterD6: bigint;
      lowerRewardD6: bigint;
      upperRewardD6: bigint;
    };

export interface BackerRewardProjectionInput {
  currentTotalBackedD6: string;
  viewerBackerShareBalanceD6: string;
  newBackingAmountD6: bigint;
  creationThresholdD6: string;
  feeBpsBuy: number;
  feeBpsSellNormal: number;
  feeSplitBackersBps: number;
  volumeD6: bigint;
}

const CANONICAL_UNSIGNED_INTEGER = /^(?:0|[1-9]\d*)$/;

export function projectBackerReward(input: BackerRewardProjectionInput): BackerRewardProjection {
  if (!isU64(input.newBackingAmountD6) || !isU64(input.volumeD6)) {
    return { status: 'unavailable', reason: 'invalid_amount' };
  }

  const currentTotalBackedD6 = parseU64(input.currentTotalBackedD6);
  const viewerBackerShareBalanceD6 = parseU64(input.viewerBackerShareBalanceD6);
  const creationThresholdD6 = parseU64(input.creationThresholdD6);
  if (
    currentTotalBackedD6 === null ||
    viewerBackerShareBalanceD6 === null ||
    creationThresholdD6 === null ||
    creationThresholdD6 === BigInt(0)
  ) {
    return { status: 'unavailable', reason: 'invalid_proposal_state' };
  }
  if (input.newBackingAmountD6 === BigInt(0) && viewerBackerShareBalanceD6 === BigInt(0)) {
    return { status: 'empty' };
  }

  const feeBpsBuy = parseBps(input.feeBpsBuy);
  const feeBpsSellNormal = parseBps(input.feeBpsSellNormal);
  const feeSplitBackersBps = parseBps(input.feeSplitBackersBps);
  if (feeBpsBuy === null || feeBpsSellNormal === null || feeSplitBackersBps === null) {
    return { status: 'unavailable', reason: 'invalid_config' };
  }

  const backedAfter = checkedAddU128(currentTotalBackedD6, input.newBackingAmountD6);
  const viewerSharesAfter = checkedAddU128(viewerBackerShareBalanceD6, input.newBackingAmountD6);
  if (!backedAfter.available || !viewerSharesAfter.available) {
    return { status: 'unavailable', reason: 'invalid_amount' };
  }

  const assumedFinalSupplyD6 =
    backedAfter.value > creationThresholdD6 ? backedAfter.value : creationThresholdD6;
  const userSharesAfterD6 =
    viewerSharesAfter.value > assumedFinalSupplyD6 ? assumedFinalSupplyD6 : viewerSharesAfter.value;
  const buyRewardD6 = rewardForFeeRate(
    input.volumeD6,
    feeBpsBuy,
    feeSplitBackersBps,
    userSharesAfterD6,
    assumedFinalSupplyD6,
  );
  const sellRewardD6 = rewardForFeeRate(
    input.volumeD6,
    feeBpsSellNormal,
    feeSplitBackersBps,
    userSharesAfterD6,
    assumedFinalSupplyD6,
  );

  return {
    status: 'ready',
    assumedFinalSupplyD6,
    userSharesAfterD6,
    lowerRewardD6: buyRewardD6 < sellRewardD6 ? buyRewardD6 : sellRewardD6,
    upperRewardD6: buyRewardD6 > sellRewardD6 ? buyRewardD6 : sellRewardD6,
  };
}

function rewardForFeeRate(
  volumeD6: bigint,
  feeRateBps: bigint,
  feeSplitBackersBps: bigint,
  userSharesD6: bigint,
  assumedFinalSupplyD6: bigint,
): bigint {
  const tradeFeeD6 = floorFee(volumeD6, feeRateBps, BPS_DENOMINATOR);
  const backerPoolD6 = splitFloorFee(tradeFeeD6, feeSplitBackersBps, BPS_DENOMINATOR).ratedAmount;
  return floorFee(backerPoolD6, userSharesD6, assumedFinalSupplyD6);
}

function parseU64(raw: string): bigint | null {
  if (!CANONICAL_UNSIGNED_INTEGER.test(raw)) return null;
  const parsed = BigInt(raw);
  return isU64(parsed) ? parsed : null;
}

function parseBps(raw: number): bigint | null {
  if (!Number.isSafeInteger(raw)) return null;
  const parsed = BigInt(raw);
  return isRate(parsed, BPS_DENOMINATOR) ? parsed : null;
}
